import { isNativeShell } from './distribution';
import { getOrCreateInstallId } from './clientSignals';
import {
  isSessionInactive,
  readLastActivityAt,
} from './sessionInactivity';

const ENABLED_KEY = 'qixit.biometric.unlock.enabled';
const PROMPT_DISMISSED_KEY = 'qixit.biometric.unlock.prompt.dismissed';
const SESSION_UNLOCKED_KEY = 'qixit.biometric.sessionUnlocked';

export type BiometricAvailability = {
  available: boolean;
  biometryType?: number;
  reason?: string;
};

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string, value: boolean): void {
  try {
    if (value) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch {
    // ignore quota / private mode
  }
}

/** User opted in to biometric unlock on this install. */
export function isBiometricUnlockEnabled(): boolean {
  return readFlag(ENABLED_KEY);
}

export function setBiometricUnlockEnabled(enabled: boolean): void {
  writeFlag(ENABLED_KEY, enabled);
  if (!enabled) {
    clearBiometricSessionUnlocked();
  }
}

/** Soft dismiss of post-login enable prompt (can still enable from settings). */
export function isBiometricEnablePromptDismissed(): boolean {
  return readFlag(PROMPT_DISMISSED_KEY);
}

export function setBiometricEnablePromptDismissed(dismissed: boolean): void {
  writeFlag(PROMPT_DISMISSED_KEY, dismissed);
}

export function isBiometricSessionUnlocked(): boolean {
  try {
    return sessionStorage.getItem(SESSION_UNLOCKED_KEY) === '1';
  } catch {
    return false;
  }
}

export function markBiometricSessionUnlocked(): void {
  try {
    sessionStorage.setItem(SESSION_UNLOCKED_KEY, '1');
  } catch {
    // ignore
  }
}

export function clearBiometricSessionUnlocked(): void {
  try {
    sessionStorage.removeItem(SESSION_UNLOCKED_KEY);
  } catch {
    // ignore
  }
}

/** True when persisted JWT session is still within the idle window. */
export function hasValidPersistedSession(): boolean {
  const accessToken = localStorage.getItem('accessToken');
  const userJson = localStorage.getItem('user');
  if (!accessToken || !userJson) return false;
  return !isSessionInactive(readLastActivityAt());
}

/**
 * Gate should cover the UI until biometrics succeed (or user chooses password login).
 * Does not require TOTP enrollment — only a valid persisted session + user opt-in.
 */
export function shouldShowBiometricUnlockGate(): boolean {
  if (!isNativeShell()) return false;
  if (!isBiometricUnlockEnabled()) return false;
  if (!hasValidPersistedSession()) return false;
  if (isBiometricSessionUnlocked()) return false;
  return true;
}

export async function checkBiometricAvailability(): Promise<BiometricAvailability> {
  if (!isNativeShell()) {
    return { available: false, reason: 'not-native' };
  }
  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
    const result = await NativeBiometric.isAvailable();
    return {
      available: !!result.isAvailable,
      biometryType: result.biometryType,
      reason: result.isAvailable ? undefined : String(result.errorCode ?? 'unavailable'),
    };
  } catch (err) {
    return {
      available: false,
      reason: err instanceof Error ? err.message : 'plugin-error',
    };
  }
}

export async function verifyBiometricUnlock(): Promise<boolean> {
  if (!isNativeShell()) return false;
  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
    await NativeBiometric.verifyIdentity({
      reason: 'Unlock Qixit Mail',
      title: 'Unlock Qixit Mail',
      subtitle: 'Confirm it is you',
      description: 'Use fingerprint or face unlock to reopen your mailbox on this device.',
      negativeButtonText: 'Use password',
      maxAttempts: 5,
    });
    markBiometricSessionUnlocked();
    return true;
  } catch {
    return false;
  }
}

/** Bind enablement to current install for diagnostics / future hardening. */
export function getBiometricUnlockMeta(): { installId: string; enabled: boolean } {
  return {
    installId: getOrCreateInstallId(),
    enabled: isBiometricUnlockEnabled(),
  };
}
